import { IsBoolean, IsDateString, IsIn, IsOptional, IsString, MaxLength } from 'class-validator';
import type { ServiceMethod } from './stage-data.dto';

/**
 * Step 5.3 — service of the Form 4. The app supports four service methods, each
 * with its own required particulars, and a file can carry more than one record
 * when service is attempted more than once. Exactly one record is `operative`:
 * the one whose date starts the 14-day appeal clock (s. 134(1)). The operative
 * record's method/date are mirrored onto the file's `step_5` slice so the
 * deadlines engine, checklist and document assembly read a single value.
 */

/** How the notice was mailed, when `method` is `mail`. */
export type MailClass = 'regular' | 'registered' | 'certified';
export const MAIL_CLASSES: MailClass[] = ['regular', 'registered', 'certified'];

/** Who accepted personal service, relative to the property. */
export type ServedRelationship = 'owner' | 'occupant' | 'agent' | 'other';
export const SERVED_RELATIONSHIPS: ServedRelationship[] = ['owner', 'occupant', 'agent', 'other'];

export interface ServiceRecordDetail {
  id: string;
  fileId: string;
  method: ServiceMethod;
  /** Date of service — the operative record's date starts the appeal clock. */
  serviceDate: string;
  /** Time of service, where the method records one (posting, personal, email). */
  serviceTime: string | null;
  /** Whether this attempt succeeded; a failed attempt stays on the file. */
  successful: boolean;
  /** The record whose service date drives the statutory clock. */
  operative: boolean;

  // ── Posting (conspicuous place on the property) ────────────────────────────
  postingLocation: string | null;
  /** Evidence row holding the photo of the posting (affidavit exhibit "B"). */
  postingEvidenceId: string | null;

  // ── Personal / hand delivery ───────────────────────────────────────────────
  personName: string | null;
  personRelationship: ServedRelationship | null;
  /** The actual relationship, when the code is `other`. Null otherwise. */
  personRelationshipOther: string | null;
  serviceLocation: string | null;

  // ── Mail ───────────────────────────────────────────────────────────────────
  mailingAddress: string | null;
  mailClass: MailClass | null;
  trackingNumber: string | null;

  // ── Email ──────────────────────────────────────────────────────────────────
  emailAddress: string | null;
  deliveryConfirmation: string | null;

  /** Officer who effected or witnessed service. */
  officerId: string | null;
  notes: string | null;
  createdAt: string;
}

export class CreateServiceRecordDto {
  @IsIn(['in_person', 'mail', 'posted', 'email'])
  method!: ServiceMethod;

  @IsDateString()
  serviceDate!: string;

  @IsOptional()
  @IsString()
  @MaxLength(16)
  serviceTime?: string | null;

  @IsOptional()
  @IsBoolean()
  successful?: boolean;

  /**
   * Make this the record that starts the appeal clock. Defaults to true for the
   * first successful record on the file.
   */
  @IsOptional()
  @IsBoolean()
  operative?: boolean;

  @IsOptional()
  @IsString()
  postingLocation?: string | null;

  @IsOptional()
  @IsString()
  postingEvidenceId?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  personName?: string | null;

  @IsOptional()
  @IsIn(SERVED_RELATIONSHIPS)
  personRelationship?: ServedRelationship | null;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  personRelationshipOther?: string | null;

  @IsOptional()
  @IsString()
  serviceLocation?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(400)
  mailingAddress?: string | null;

  @IsOptional()
  @IsIn(MAIL_CLASSES)
  mailClass?: MailClass | null;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  trackingNumber?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(320)
  emailAddress?: string | null;

  @IsOptional()
  @IsString()
  deliveryConfirmation?: string | null;

  @IsOptional()
  @IsString()
  notes?: string | null;
}

/** Partial update of an existing service record (same fields, all optional). */
export class UpdateServiceRecordDto {
  @IsOptional()
  @IsIn(['in_person', 'mail', 'posted', 'email'])
  method?: ServiceMethod;

  @IsOptional()
  @IsDateString()
  serviceDate?: string;

  @IsOptional()
  @IsString()
  @MaxLength(16)
  serviceTime?: string | null;

  @IsOptional()
  @IsBoolean()
  successful?: boolean;

  @IsOptional()
  @IsBoolean()
  operative?: boolean;

  @IsOptional()
  @IsString()
  postingLocation?: string | null;

  @IsOptional()
  @IsString()
  postingEvidenceId?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  personName?: string | null;

  @IsOptional()
  @IsIn(SERVED_RELATIONSHIPS)
  personRelationship?: ServedRelationship | null;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  personRelationshipOther?: string | null;

  @IsOptional()
  @IsString()
  serviceLocation?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(400)
  mailingAddress?: string | null;

  @IsOptional()
  @IsIn(MAIL_CLASSES)
  mailClass?: MailClass | null;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  trackingNumber?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(320)
  emailAddress?: string | null;

  @IsOptional()
  @IsString()
  deliveryConfirmation?: string | null;

  @IsOptional()
  @IsString()
  notes?: string | null;
}
